/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'
import { useRouter } from 'next/navigation'
import React, { Dispatch, SetStateAction, useCallback, useState } from 'react'

import defaultLocale from '@/stores/defaultLocale'
import getLocale from '@/stores/locales'

import { UseLoggedContext } from '@mercadoni/sinister/dist/cjs/authentication'
import { UseClientModelContext } from '@mercadoni/sinister/dist/cjs/client'
import { DrawerCartV3 } from '@mercadoni/sinister/dist/cjs/drawerCart' // TODO: review size of this component
import useInteraction from '@mercadoni/sinister/dist/cjs/monitors/useInteractionSSR'
import ProductInCart from '@mercadoni/sinister/dist/cjs/productv2ProductInCart' // TODO: review size of this component
import { getProductListInfo } from '@mercadoni/sinister/dist/cjs/productv2Utils'
import { useHeaderDataContext } from '@mercadoni/sinister/dist/cjs/shared/contexts'
import { isEnabledProductsSummary } from '@mercadoni/sinister/dist/cjs/shared/utils'
import useCartV3 from '@mercadoni/sinister/dist/cjs/useCartV3'

import useGuessAction from '@/hooks/useGuessAction'
import goToProduct from '@/navigation/CSR/goToProduct'
import { useAddressContext } from '../AddressContext'
import { useAuthContext } from '../AuthContext'
import { useOperationalModelContext } from '../OperationalModelContext'
import { useStoreContext } from '../StoreContext'

const DrawerControlContext = React.createContext<{
  onInitOpenDrawer: (value: boolean) => void
  setOpenDrawer: (value: boolean) => void
  setOnCheckoutClick: Dispatch<SetStateAction<() => void>>
  setOnClose: Dispatch<SetStateAction<() => void>>
  setCustomerWillCheckout: (value: boolean) => void
}>({
  onInitOpenDrawer: () => {},
  setOpenDrawer: () => {},
  setOnCheckoutClick: () => {},
  setOnClose: () => {},
  setCustomerWillCheckout: () => {},
})

export const DrawerControlProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const clientModel = UseClientModelContext()
  const {
    enhanceImg,
    clientId,
    currencyCode,
    hasProductsSummary,
    showAddressLabel,
  } = clientModel
  const { isLogged } = UseLoggedContext()
  const [openDrawer, setOpenDrawer] = useState(false)
  const router = useRouter()
  const [onClose, setOnClose] = useState<() => void>(() => () => {})
  const [onCheckoutClick, setOnCheckoutClick] = useState<() => void>(
    () => () => {}
  )
  const [customerWillCheckout, setCustomerWillCheckout] = useState(false)
  const [prevHeaderData, setPrevHeaderData] = useState({})
  const onGuessAction = useGuessAction({ router, forceToLogin: false })
  const { headerData, setHeaderData, screenNames } = useHeaderDataContext()
  const cartHeaderData = screenNames?.['/cart']

  const { customer } = useAuthContext()
  const { store } = useStoreContext()
  const { operationalModel } = useOperationalModelContext()
  const { address } = useAddressContext()

  const initialParams = {
    clientId,
    currencyCode,
    customer,
    opsmodel: operationalModel,
    address,
    store,
    headerData,
    guestId:
      typeof window !== 'undefined'
        ? window?.getCookie?.(window.COOKIE_KEY) || ''
        : '',
    path: cartHeaderData?.path,
    screenName: cartHeaderData?.screenName,
  }

  const { sendInteractionEvent } = useInteraction(initialParams)
  const copies = getLocale() || defaultLocale
  const enabledProductsSummary = isEnabledProductsSummary(hasProductsSummary)
  const listInfo = {
    screen_name: cartHeaderData?.screenName,
    ...getProductListInfo('cart'),
  }

  const onInitOpenDrawer = (value: boolean) => {
    setOpenDrawer(value)
    if (value) {
      setHeaderData(cartHeaderData)
      setPrevHeaderData(headerData)
    }
  }

  const renderProductInCart = (props: { product: string }) => (
    <ProductInCart
      enhanceImg={enhanceImg}
      copies={copies}
      {...props}
      listInfo={listInfo}
      onInteraction={sendInteractionEvent}
      onCarlessProduct={onGuessAction}
      isLogged={isLogged}
    />
  )
  const redirectToProduct = useCallback(
    (slug: string) => {
      onClose()
      goToProduct(router, slug)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose]
  )

  const onCloseFn = (props: { action?: string }) => {
    const { action } = props || {}
    if (action !== 'checkout') {
      setHeaderData(prevHeaderData)
    }
    setOpenDrawer(false)
    setCustomerWillCheckout(false)
    onClose()
  }

  return (
    <DrawerControlContext.Provider
      value={{
        onInitOpenDrawer,
        setOpenDrawer,
        setOnCheckoutClick,
        setOnClose,
        setCustomerWillCheckout,
      }}
    >
      {children}
      <DrawerCartV3
        open={openDrawer}
        onCheckoutClick={onCheckoutClick}
        onClose={onCloseFn}
        customerWillCheckout={customerWillCheckout}
        setCustomerWillCheckout={setCustomerWillCheckout}
        renderProductInCart={renderProductInCart}
        headerData={cartHeaderData}
        useCart={useCartV3}
        clientId={clientId}
        redirectToProduct={redirectToProduct}
        currency={currencyCode}
        enabledProductsSummary={enabledProductsSummary}
        copies={copies}
        clientModel={clientModel}
        showAddressLabel={showAddressLabel}
      />
    </DrawerControlContext.Provider>
  )
}

export const useDrawerControlContext = () => {
  const context = React.useContext(DrawerControlContext)
  if (!context) {
    throw new Error('DrawerControlContext: no context')
  }
  return context
}
